
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowLeft, Calendar, Clock, CreditCard } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';

interface BookingDetails {
  id: string;
  partner_id: string;
  session_time: string;
  session_duration: number;
  paid_amount: number;
  currency: string;
  partner_name?: string;
  razorpay_payment_id?: string;
}

const BookingPaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Get booking ID from URL parameters or location state
  const params = new URLSearchParams(location.search);
  const bookingId = params.get('booking') || (location.state?.bookingId as string);
  
  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) {
        setLoading(false);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id,
            partner_id,
            session_time,
            session_duration,
            paid_amount,
            razorpay_payment_id,
            partners:partner_id (name)
          `)
          .eq('id', bookingId)
          .eq('payment_status', 'completed')
          .single();
          
        if (error) {
          console.error('Error fetching booking:', error);
          setLoading(false);
          return;
        }
        
        setBooking({
          id: data.id,
          partner_id: data.partner_id,
          session_time: data.session_time,
          session_duration: data.session_duration,
          paid_amount: data.paid_amount,
          currency: 'INR', // Default currency, could be stored in booking
          partner_name: data.partners?.name,
          razorpay_payment_id: data.razorpay_payment_id
        });
      } catch (error) {
        console.error('Failed to fetch booking details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookingDetails();
  }, [bookingId]);
  
  const handleGoBack = () => {
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-lg">Loading booking details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-6 text-muted-foreground flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          
          <Card>
            <CardHeader className="text-center border-b pb-8">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-700">Booking Confirmed!</CardTitle>
              <CardDescription className="text-base">
                Your session has been booked successfully
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-6">
              {booking ? (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium text-lg">Booking Details</h3>
                    
                    <div className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Booking ID:</span>
                        <span className="font-medium">{booking.id}</span>
                      </div>
                      
                      {booking.razorpay_payment_id && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Transaction ID:</span>
                          <span className="font-medium">{booking.razorpay_payment_id}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Counselor:</span>
                        <span className="font-medium">{booking.partner_name || "Not specified"}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Date:</span>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-muted-foreground" />
                          <span>{formatDate(booking.session_time)}</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Time:</span>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
                          <span>{formatTime(booking.session_time)}</span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-2 border-t mt-2">
                        <span className="font-medium">Amount Paid:</span>
                        <span className="font-bold">{booking.paid_amount} {booking.currency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-lg">What's Next?</h3>
                    <p className="text-muted-foreground">
                      Your counselor will be notified of your booking. You'll receive an email confirmation with instructions on how to join the session.
                    </p>
                  </div>
                  
                  <div className="pt-4 flex justify-center">
                    <Button onClick={handleGoBack}>Return to Home</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">Booking details not available.</p>
                  <Button onClick={handleGoBack}>Return to Home</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BookingPaymentSuccess;
